import { cxDynamic, cxRecent, cxSingle, cxScoped, cxEvaluated } from '@proc7ts/context-values';
import { onSupplied, onEventBy, EventEmitter, onceOn, trackValue, isAfterEvent, mapAfter, afterValue, mapAfter_, supplyAfter, deduplicateAfter_, afterAll, digAfter_, translateAfter_, afterThe, deduplicateAfter, afterEventBy, sendEventsTo, AfterEvent__symbol, trackValueBy } from '@proc7ts/fun-events';
import { DomEventDispatcher } from '@frontmeans/dom-events';
import { CxWindow, PreRenderScheduler } from '@frontmeans/render-scheduler';
import { asis, valueByRecipe, lazyValue, arrayOfElements, valueRecipe, noop, valueProvider, valuesProvider } from '@proc7ts/primitives';
import { Supply } from '@proc7ts/supply';
import { DrekFragment, drekCharger, drekAppender } from '@frontmeans/drek';
import { ComponentContext, ComponentRenderCtl, ComponentMember, BootstrapContext, ComponentSlot } from '@wesib/wesib';
import { nodeHost } from '@frontmeans/dom-primitives';
import { NamespaceAliaser, html__naming } from '@frontmeans/namespace-aliaser';
import { cxTrackAsset } from '@proc7ts/context-builder';
import { allAmender, newAmendTarget } from '@proc7ts/amend';

function cxFetchAgent() {
    return cxDynamic({
        create: FetchAgent$combine,
        byDefault: _ => FetchAgent$default,
        assign: ({ get, to }) => {
            const agent = (next, request) => get()(next, request);
            return receiver => to((_, by) => receiver(agent, by));
        },
    });
}
function FetchAgent$default(next, request) {
    return next(request);
}
function FetchAgent$combine(agents, _target) {
    return (next, request) => {
        const fetch = (agentIdx, agentRequest) => {
            const agent = agents[agentIdx];
            if (!agent) {
                return next(agentRequest);
            }
            return onSupplied(agent((nextRequest = agentRequest) => fetch(agentIdx + 1, nextRequest), agentRequest));
        };
        return fetch(0, request);
    };
}

/**
 * Context entry containing an {@link HttpFetchAgent} instance.
 *
 * The agent returned combines all registered agents into one. If no agent registered it just performs the fetch.
 */
const HttpFetchAgent = {
    perContext: ( /*#__PURE__*/cxFetchAgent()),
    toString: () => '[HttpFetchAgent]',
};

/**
 * Bootstrap context entity containing an {@link HttpFetch} instance.
 */
const HttpFetch = {
    perContext: ( /*#__PURE__*/cxRecent({
        create: asis,
        byDefault: HttpFetch$byDefault,
        assign: ({ get, to }) => {
            const fetch = (input, init) => get()(input, init);
            return receiver => to((_, by) => receiver(fetch, by));
        },
    })),
    toString: () => '[HttpFetch]',
};
const HttpFetchAborted = {};
function HttpFetch$byDefault(target) {
    const window = target.get(CxWindow);
    const agent = target.get(HttpFetchAgent);
    return (input, init) => agent(fetch, new Request(input, init));
    function fetch(request) {
        return onEventBy(receiver => {
            const responseEmitter = new EventEmitter();
            let supply;
            if ('AbortController' in window) {
                const abortController = new window.AbortController();
                const { signal } = abortController;
                supply = new Supply(reason => {
                    if (reason === HttpFetchAborted) {
                        abortController.abort();
                    }
                });
                receiver.supply.whenOff(() => supply.off(HttpFetchAborted)).needs(supply);
                responseEmitter.on({
                    supply,
                    receive(ctx, response) {
                        receiver.receive(ctx, response);
                    },
                });
                const customSignal = request.signal;
                if (customSignal) {
                    new DomEventDispatcher(customSignal)
                        .on('abort')
                        .do(onceOn)(() => abortController.abort());
                    if (customSignal.aborted) {
                        abortController.abort();
                    }
                }
                request = new Request(request, { signal });
            }
            else {
                supply = responseEmitter.on(receiver);
            }
            window.fetch(request)
                .then(response => {
                responseEmitter.send(response);
                supply.off();
            })
                .catch(reason => supply.off(reason));
        });
    }
}

/**
 * Component context entry containing {@link FragmentRenderCtl fragment render control}.
 */
const FragmentRenderCtl = {
    perContext: ( /*#__PURE__*/cxSingle({
        byDefault: target => new FragmentRenderCtl$(target.get(ComponentContext)),
    })),
    toString: () => '[FragmentRenderCtl]',
};
const RenderFragment$done = {};
class FragmentRenderCtl$ {
    constructor(_context) {
        this._context = _context;
    }
    renderFragmentBy(renderer, def = {}) {
        const spec = valueByRecipe(def, this._context);
        const doRenderFragment = spec.settle === false
            ? RenderFragment$justRender
            : RenderFragment$settleThenRender;
        const renderFragment = (fragment, retainContent) => {
            if (!retainContent) {
                doRenderFragment(fragment);
            }
        };
        const { target = RenderFragment$defaultTarget } = spec;
        const getTarget = lazyValue(() => target(this._context));
        const renderCtl = this._context.get(ComponentRenderCtl);
        const scheduler = this._context.get(PreRenderScheduler);
        const supply = new Supply();
        const renderSupply = renderCtl.preRenderBy(preExec => {
            const fragment = new DrekFragment(getTarget(), { scheduler });
            let retainContent = false;
            const exec = {
                ...preExec,
                supply,
                fragment,
                content: fragment.content,
                postpone(postponed) {
                    preExec.postpone(() => postponed(exec));
                },
                renderBy(renderer) {
                    preExec.renderBy(renderExec => {
                        renderExec.renderBy(renderer);
                    });
                },
                retainContent(retain = true) {
                    retainContent = retain;
                },
                done() {
                    preExec.renderBy(({ supply }) => {
                        renderSupply.as(supply).off(RenderFragment$done);
                    });
                },
            };
            renderer(exec);
            renderFragment(fragment, retainContent);
        }, spec).needs(supply).whenOff(reason => {
            if (reason !== RenderFragment$done) {
                supply.off(reason);
            }
        });
        return supply;
    }
}
function RenderFragment$defaultTarget({ contentRoot }) {
    return drekCharger(drekAppender(contentRoot));
}
function RenderFragment$settleThenRender(fragment) {
    const { innerContext } = fragment;
    const { window } = innerContext;
    innerContext.scheduler()(() => {
        window.customElements.upgrade(fragment.content);
    });
    fragment.settle();
    fragment.render();
}
function RenderFragment$justRender(fragment) {
    fragment.render();
}

/**
 * Creates a {@link RenderFragmentDef.Method fragment renderer method} amendment (amd decorator).
 *
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Non-mandatory rendering definition.
 *
 * @returns New component method amendment.
 */
function RenderFragment(def) {
    return ComponentMember(({ key, get, amend }) => amend({
        componentDef: {
            define(defContext) {
                defContext.whenComponent((context) => {
                    context.whenReady(() => {
                        const { component } = context;
                        const renderer = get(component).bind(component);
                        context.get(FragmentRenderCtl).renderFragmentBy(renderer, RenderFragment$def(context, key, def));
                    });
                });
            },
        },
    }));
}
function RenderFragment$def(context, key, def = {}) {
    const spec = valueByRecipe(def, context);
    const { target = ({ contentRoot }) => drekCharger(drekAppender(contentRoot), RenderFragment$defaultRem(key)) } = spec;
    return { ...spec, target };
}
function RenderFragment$defaultRem(key) {
    const rem = String(key);
    return rem.startsWith('render') ? rem.substr(6) : rem;
}

const ShareRegistry$perContext = ( /*#__PURE__*/cxScoped(BootstrapContext, cxEvaluated(target => new ShareRegistry(target.get(NamespaceAliaser)))));
class ShareRegistry {
    constructor(nsAlias) {
        this.nsAlias = nsAlias;
        this._sharers = new Map();
    }
    static perContext(target) {
        return ShareRegistry$perContext(target);
    }
    static toString() {
        return '[ShareRegistry]';
    }
    addSharer(share, componentType, elementName, supply) {
        let sharers = this._sharers.get(share);
        if (!sharers) {
            sharers = Sharers$new();
            this._sharers.set(share, sharers);
            Sharers$addSharer(sharers, componentType, supply);
            Sharers$addName(sharers, elementName, supply);
        }
        else {
            Sharers$addSharer(sharers, componentType, supply);
            Sharers$addName(sharers, elementName, supply);
            sharers.it = { ...sharers.it };
        }
    }
    sharers(share) {
        let sharers = this._sharers.get(share);
        if (!sharers) {
            sharers = Sharers$new();
            this._sharers.set(share, sharers);
        }
        return sharers;
    }
}
function Sharers$new() {
    return trackValue({ names: new Map(), sharers: new Map() });
}
function Sharers$addName(tracker, name, supply) {
    if (!name) {
        return;
    }
    const sharers = tracker.it;
    const counter = sharers.names.get(name) || 0;
    sharers.names.set(name, counter + 1);
    supply.whenOff(() => {
        const counter = sharers.names.get(name) - 1;
        if (counter > 0) {
            sharers.names.set(name, counter);
        }
        else {
            sharers.names.delete(name);
        }
        tracker.it = { ...sharers };
    });
}
function Sharers$addSharer(tracker, componentType, supply) {
    const sharers = tracker.it;
    const counter = sharers.sharers.get(componentType) || 0;
    sharers.sharers.set(componentType, counter + 1);
    supply.whenOff(() => {
        const counter = sharers.sharers.get(componentType) - 1;
        if (counter > 0) {
            sharers.sharers.set(componentType, counter);
        }
        else {
            sharers.sharers.delete(componentType);
        }
        tracker.it = { ...sharers };
    });
}

const Share$impl__symbol = ( /*#__PURE__*/Symbol('Share.impl'));
class Share$ {
    constructor(_share, name, options) {
        this._share = _share;
        this.name = name;
        this._aliases = arrayOfElements(options.as).map(alias => alias.share);
    }
    addSharer(defContext, options = {}) {
        const { local, name = defContext.elementDef.name } = options;
        const registry = defContext.get(BootstrapContext).get(ShareRegistry);
        const supply = new Supply();
        const { componentType } = defContext;
        const elementName = local
            ? undefined
            : name && html__naming.name(name, registry.nsAlias).toLowerCase();
        registry.addSharer(this._share, componentType, elementName, supply);
        for (const alias of this._aliases) {
            registry.addSharer(alias, componentType, elementName, supply);
        }
        return supply;
    }
    shareValue(registrar) {
        registrar.shareAs(this._share);
        const priorityOffset = registrar.priority + 1;
        this._aliases.forEach((alias, index) => {
            alias.shareValue(registrar.withPriority(priorityOffset + index));
        });
    }
}

/**
 * A key of the {@link SharedValue.Detailed detailed shared value descriptor} containing the
 * {@link SharedValue.Details shared value details}.
 */
const SharedValue__symbol = ( /*#__PURE__*/Symbol('SharedValue'));
const SharedValue = {
    /**
     * Checks whether the given value shared by component is has details.
     *
     * @typeParam T - Shared value type.
     * @param value - Shared value to check.
     *
     * @returns `true` if the given value is an object implementing a {@link SharedValue.Detailed} interface.
     */
    hasDetails(value) {
        return !!value
            && typeof value === 'object'
            && typeof value[SharedValue__symbol] === 'object';
    },
};

/**
 * Checks whether the given value is {@link SharerAware aware} of its sharer.
 *
 * @param value - A value to check.
 *
 * @returns `true` if `value` has {@link SharerAware.sharedBy sharedBy} method, or `false` otherwise.
 */
function isSharerAware(value) {
    return (typeof value === 'object' && !!value || typeof value === 'function')
        && typeof value.sharedBy === 'function';
}
/**
 * Informs the given value on its sharer.
 *
 * Does nothing if the `value` is not {@link SharerAware aware} of its sharer.
 *
 * @param sharer - Sharer component context.
 * @param value - Shared value.
 *
 * @returns The `value` itself.
 */
function shareValueBy(sharer, value) {
    if (isSharerAware(value)) {
        value.sharedBy(sharer);
    }
    return value;
}

function SharedValue$ContextBuilder(share, provider) {
    return {
        entry: share,
        setupAsset(target) {
            const registrar = share.createRegistrar(target, provider);
            share.shareValue(registrar);
            return registrar.supply;
        },
    };
}
function SharedValue$Registrar(target, provider) {
    return SharedValue$BoundRegistrar(target, SharedValue$bindProvider(provider));
}
function SharedValue$BoundRegistrar(target, provider) {
    const { priority, supply, provide } = provider;
    return {
        priority,
        supply,
        shareAs: ({ share }, newPriority = priority) => {
            newPriority = Math.max(0, newPriority);
            target.provide(newPriority
                ? SharedValue$placeDetailed(share, provide, newPriority)
                : SharedValue$placeBare(share, provide));
        },
        withPriority: newPriority => SharedValue$BoundRegistrar(target, { ...provider, priority: Math.max(0, newPriority) }),
    };
}
function SharedValue$bindProvider(provider) {
    const priority = provider.priority ? Math.max(0, provider.priority) : 0;
    const { supply = new Supply() } = provider;
    return {
        priority,
        supply,
        provide: (target) => {
            const valueOrKeeper = provider.provide(target);
            return isAfterEvent(valueOrKeeper)
                ? valueOrKeeper.do(mapAfter((value) => shareValueBy(target.context, value)))
                : shareValueBy(target.context, valueOrKeeper);
        },
    };
}
function SharedValue$placeBare(share, provider) {
    return cxTrackAsset(share, (target, receiver, supply) => {
        afterValue(provider(target))({
            receive(_, asset) {
                if (asset !== undefined) {
                    receiver(asset);
                }
            },
            supply,
        });
    });
}
function SharedValue$placeDetailed(share, provider, priority) {
    return cxTrackAsset(share, (target, receiver, supply) => {
        afterValue(provider(target))({
            receive(_, asset) {
                if (asset !== undefined) {
                    receiver({
                        [SharedValue__symbol]: {
                            priority,
                            value: asset,
                        },
                    });
                }
            },
            supply,
        });
    });
}

/**
 * A kind of the value a component shares with the nested ones.
 *
 * The sharing implies the following:
 *
 * - The sharer component {@link addSharer registers} its element name as the one bound to sharer.
 * - The sharer component {@link shareValue provides} an (updatable) shared value within its context.
 * - The consumer component {@link valueFor obtains} the shared value by searching the parent element with a sharer
 *   bound to it.
 *
 * A share instance is used as an identifier in all these steps.
 *
 * A {@link Shared @Shared} component property decorator may be used to automate this.
 *
 * @typeParam T - Shared value type.
 */
class Share {
    /**
     * Constructs new component share.
     *
     * @param name - A human-readable name of the share.
     * @param options - Constructed share options.
     */
    constructor(name, options = {}) {
        this[Share$impl__symbol] = new Share$(this, name, options);
    }
    /**
     * Refers to itself.
     */
    get share() {
        return this;
    }
    /**
     * A human-readable name of the name.
     */
    get name() {
        return this[Share$impl__symbol].name;
    }
    get [Symbol.toStringTag]() {
        return this.name;
    }
    perContext(target) {
        const track = target.lazy(target => Share$track(this, target));
        return {
            assign(receiver) {
                receiver(track());
            },
        };
    }
    /**
     * Registers a sharer component.
     *
     * The registration is necessary for consumers to be able to find the element bound to sharer by that element's name.
     *
     * @param defContext - The definition context of the sharer component.
     * @param options - Value sharing options.
     *
     * @returns Sharer registration supply. Revokes the sharer registration once cut off.
     */
    addSharer(defContext, options) {
        return this[Share$impl__symbol].addSharer(defContext, options);
    }
    /**
     * Shares a value by providing it for the sharer component context.
     *
     * @param registrar - Shared value registrar.
     *
     * @return A builder of shared value for component context.
     */
    shareValue(registrar) {
        this[Share$impl__symbol].shareValue(registrar);
    }
    /**
     * Creates a shared value registrar that shares a value created by the given provider.
     *
     * @typeParam TSharer - Sharer component type.
     * @param target - Shared value definition target.
     * @param provider - Shared value provider.
     *
     * @returns New shared value registrar.
     */
    createRegistrar(target, provider) {
        return SharedValue$Registrar(target, provider);
    }
    /**
     * Locates a shared value for the consuming component.
     *
     * Searches among parent elements for the one bound to the sharer component, then obtains the shared value from
     * the sharer's context.
     *
     * @param consumer - Consumer component context.
     * @param options - Location options.
     *
     * @returns An `AfterEvent` keeper of the shared value and its sharer context, if found.
     */
    valueFor(consumer, options = {}) {
        const { host = nodeHost, local } = options;
        const sharers = consumer.get(BootstrapContext).get(ShareRegistry).sharers(this);
        const status = consumer.readStatus.do(deduplicateAfter_((a, b) => a === b, Share$consumerStatus));
        return afterAll({
            sharers,
            status,
        }).do(digAfter_(({ sharers: [sharers] }) => {
            if (local) {
                if (sharers.sharers.has(consumer.componentType)) {
                    return Share$sharedValue(this, consumer);
                }
                if (local === true) {
                    return afterThe();
                }
            }
            let element = host(consumer.element);
            while (element) {
                if (sharers.names.has(element.tagName.toLowerCase())) {
                    return ComponentSlot.of(element).read.do(digAfter_(sharer => sharer ? Share$sharedValue(this, sharer) : afterThe()));
                }
                element = host(element);
            }
            return afterThe();
        }), deduplicateAfter());
    }
    /**
     * Selects a shared value among candidates.
     *
     * It is especially useful when the value shared by multiple sharers.
     *
     * By default:
     *
     * - Prefers bare value.
     * - Prefers the value from {@link SharedValue.Detailed detailed specifier} with higher priority
     *   (i.e. lesser {@link SharedValue.Details.priority priority value}).
     * - Prefers the value declared last.
     *
     * @param values - The values shared by sharers. May contain a {@link SharedValue.Detailed detailed value
     * specifiers} in addition to pure values.
     *
     * @returns Either selected value, or `undefined` when not present.
     */
    selectValue(...values) {
        let selected;
        for (let i = values.length - 1; i >= 0; --i) {
            const value = values[i];
            if (!SharedValue.hasDetails(value)) {
                return value;
            }
            const details = value[SharedValue__symbol];
            if (!selected || selected.priority > details.priority) {
                selected = details;
            }
        }
        return selected && selected.value;
    }
    toString() {
        return `[Share ${this[Symbol.toStringTag]}]`;
    }
}
function Share$track(share, target) {
    const shared = afterEventBy(receiver => {
        const dispatch = sendEventsTo(receiver);
        target.trackAssetList(assetList => dispatch(...assetList.flatMap(provided => {
            const assets = [];
            provided.eachAsset((asset) => {
                assets.push(asset);
            });
            return assets;
        })));
    });
    return shared.do(mapAfter_((...values) => share.selectValue(...values)), supplyAfter(target.supply));
}
function Share$consumerStatus([{ settled, connected }]) {
    return connected ? 2 : settled ? 1 : 0;
}
function Share$sharedValue(share, sharer) {
    return sharer.get(share).do(translateAfter_((send, value) => value ? send(value, sharer) : send()));
}

function shareLocator(locator, defaultSpec = {}) {
    if (isCustomShareLocator(locator)) {
        const { host: hostByDefault = nodeHost, local: localByDefault = false, share: shareByDefault, } = defaultSpec;
        return (consumer, options = {}) => {
            const { share = shareByDefault, host = hostByDefault, local = localByDefault, } = options;
            return locator(consumer, { share, host, local });
        };
    }
    const { share: shareRef = defaultSpec.share, host: hostByDefault = defaultSpec.host, local: localByDefault = defaultSpec.local, } = locator || {};
    const share = shareRef.share;
    return (consumer, options = {}) => {
        const { host = hostByDefault, local = localByDefault } = options;
        return share.valueFor(consumer, { host, local });
    };
}
function isCustomShareLocator(locator) {
    return typeof locator === 'function' && !('share' in locator);
}

const Shareable$Internals__symbol = ( /*#__PURE__*/Symbol('Shareable.internals'));
/**
 * Abstract implementation of value shareable by component.
 *
 * Shareable instance contains a {@link body} that become usable only when bound to sharer component.
 *
 * @typeParam TBody - Shareable body type.
 * @typeParam TSharer - Sharer component type.
 */
class Shareable {
    /**
     * Constructs shareable instance.
     *
     * @param body - Either shareable body, or its provider.
     */
    constructor(body) {
        this[Shareable$Internals__symbol] = new Shareable$Internals(this, body);
    }
    /**
     * Converts shareable body or its provider to provider that always returns an `AfterEvent` keeper of shareable body.
     *
     * @typeParam TBody - Shareable body type.
     * @typeParam TSharer - Sharer component type.
     * @param body - Either shareable body, or its provider.
     *
     * @returns Shareable body provider.
     */
    static provider(body) {
        const provider = valueRecipe(body);
        return context => afterValue(provider(context));
    }
    /**
     * Sharer component context.
     *
     * Accessing it throws an exception until bound to sharer.
     */
    get sharer() {
        return this[Shareable$Internals__symbol].sharer();
    }
    /**
     * An `AfterEvent` keeper of shareable body.
     *
     * An `[AfterEvent__symbol]` method always returns this value.
     */
    get read() {
        return this[Shareable$Internals__symbol].get().read;
    }
    /**
     * Informs this shareable instance on its sharer component.
     *
     * @param sharer - Sharer component context.
     */
    sharedBy(sharer) {
        this[Shareable$Internals__symbol].sharedBy(sharer);
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
    /**
     * Shareable body.
     *
     * Accessing is throws an exception until bound to sharer.
     */
    get body() {
        return this[Shareable$Internals__symbol].get().it;
    }
}
class Shareable$Internals {
    constructor(_source, body) {
        this._source = _source;
        this._get = Shareable.provider(body);
    }
    sharer() {
        this._notBound();
    }
    get() {
        this._notBound();
    }
    sharedBy(sharer) {
        this.sharedBy = noop;
        this.sharer = valueProvider(sharer);
        this.get = () => {
            const tracker = trackValueBy(this._get(sharer));
            this.get = valueProvider(tracker);
            return tracker;
        };
    }
    _notBound() {
        throw new TypeError(`${String(this._source)} is not properly shared yet`);
    }
}

class ShareAccessor {
    constructor(target, component) {
        this._get = target.get.bind(undefined, component);
        this._set = target.writable ? target.set.bind(undefined, component) : noop;
        this._ctx = ComponentContext.of(component);
        const value = this._get();
        this.val = trackValue(value);
        this.val.supply.needs(this._ctx);
        // Inform on sharer as the very first operation.
        this.val.read(value => shareValueBy(this._ctx, value));
        this._syncVal();
    }
    get() {
        return this.val.it;
    }
    set(value) {
        this.val.it = value;
        this._syncVal();
    }
    _syncVal() {
        if (!this._valSupply) {
            this._valSupply = this.val.read(value => this._set(value));
        }
    }
}

/**
 * Creates an amendment (and decorator) of component member that {@link Share shares} its value.
 *
 * Applies current component context to {@link SharerAware sharer-aware} values.
 *
 * @typeParam T - Shared value type.
 * @typeParam TClass - A type of decorated component class.
 * @param share - Target component share.
 * @param amendments - Amendments to apply.
 *
 * @returns New shared member amendment.
 */
function Shared(share, ...amendments) {
    const { share: { share: share$default }, local: localShare$default = false } = share;
    return ComponentMember(baseTarget => {
        const accessorKey = Symbol(`${String(baseTarget.key)}:shared`);
        let lastTarget = baseTarget;
        const accessorOf = (component) => component[accessorKey]
            || (component[accessorKey] = new ShareAccessor(lastTarget, component));
        const getShared = (component) => accessorOf(component).val.read;
        const lastAmender = (target) => {
            lastTarget = target;
            target.amend({
                get: component => accessorOf(component).get(),
                set: target.writable
                    ? (component, value) => accessorOf(component).set(value)
                    : undefined,
                componentDef: {
                    setup(setup) {
                        setup.perComponent(SharedValue$ContextBuilder(target.share, {
                            provide: ({ context }) => context.onceReady.do(digAfter_(({ component }) => accessorOf(component).val, valuesProvider())),
                        }));
                    },
                    define(defContext) {
                        target.share.addSharer(defContext, { local: target.localShare });
                    },
                },
            });
        };
        allAmender([...amendments, lastAmender])(newAmendTarget({
            base: {
                ...baseTarget,
                share: share$default,
                localShare: localShare$default,
                getShared,
            },
            amend(base, request = {}) {
                const { share = base.share, localShare = base.localShare, getShared: $getShared, ...baseRequest } = request;
                const createBaseTarget = baseTarget.amend(baseRequest);
                return () => ({
                    ...createBaseTarget(),
                    share,
                    localShare,
                    getShared,
                });
            },
        }));
    });
}

export { FragmentRenderCtl, HttpFetch, HttpFetchAgent, RenderFragment, Share, Shareable, Shared, SharedValue, SharedValue__symbol, cxFetchAgent, isSharerAware, shareLocator, shareValueBy };
//# sourceMappingURL=wesib.generic.js.map
